import React from 'react';
import { handleActions } from 'redux-actions';
import { get, set, pickBy } from 'lodash';
import * as ActionTypes from '../constants/ActionTypes';
import { sbemShadCNTheme } from '../constants/themes/sbemShadCNTheme';
import { generateShadowSizes } from '../components/pages/in-context/SidebarNavigation/utils/shadowUtils';
import { FontFamilyProperty } from '../../lib/css-property-parser/FontFamilyProperty';
import { webSafeFontsNames } from '../constants/availableFonts';

/**
 * Font tracking configuration for in-context themes
 * This tracks font families used in the light theme
 */
const inContextFontTracking = [
    'font-titles',
    'font-body'
];

/**
 * Calculate font usage for in-context themes
 * This tracks fonts across the entire theme (light, dark, etc.)
 * @param {Object} inContextTheme - The complete in-context theme object
 * @param {Object} availableFonts - Available fonts from global state
 * @returns {Object} - Calculated font usage
 */
const calculateInContextFontUsage = (inContextTheme, availableFonts = {}) => {
    const fontsUsage = {};
    
    // Track fonts from light theme
    if (inContextTheme.light) {
        inContextFontTracking.forEach(fontProperty => {
            const fontFamilies = new FontFamilyProperty(get(inContextTheme.light, fontProperty, ''));
            if (!fontFamilies.error) {
                fontFamilies.value.forEach(fontFamily => {
                    if (fontFamily) {
                        const fontLibrary = get(availableFonts, [fontFamily, 'files'], {});
                        
                        Object.keys(fontLibrary).forEach(fontVariant => {
                            const files = get(fontLibrary, [fontVariant], []);
                            fontsUsage[`${fontFamily}:${fontVariant}`] = {
                                files: files,
                                isWebSafe: webSafeFontsNames.indexOf(fontFamily) !== -1
                            };
                        });
                    }
                });
            }
        });
    }
    
    // TODO: When dark theme is implemented, track fonts from dark theme as well
    // if (inContextTheme.dark) {
    //     inContextFontTracking.forEach(fontProperty => {
    //         const fontFamilies = new FontFamilyProperty(get(inContextTheme.dark, fontProperty, ''));
    //         // ... same logic as light theme
    //     });
    // }
    
    // Return only non-web-safe fonts with files
    return pickBy(fontsUsage, o => !o.isWebSafe && o.files && o.files.length > 0);
};

const initialState = {
    ...sbemShadCNTheme
};

const actions = {
    updateLightTheme: (key, value) => (dispatch, getState) => {
        dispatch({
            type: ActionTypes.INCONTEXT_THEME_UPDATE_LIGHT,
            payload: { key, value }
        });
        
        // If a font property was updated, automatically calculate font usage
        if (inContextFontTracking.includes(key)) {
            // Get the complete state to access available fonts
            const state = getState();
            const availableFonts = state.theme.fonts || {};
            const updatedInContextTheme = {
                ...state.app.data.inContextTheme,
                light: {
                    ...state.app.data.inContextTheme.light,
                    [key]: value
                }
            };
            const calculatedFontsUsage = calculateInContextFontUsage(updatedInContextTheme, availableFonts);
            
            dispatch({
                type: ActionTypes.INCONTEXT_THEME_UPDATE_FONTS,
                payload: { fonts: calculatedFontsUsage }
            });
        }
        
        // Also dispatch EDIT_STORE_CHANGED to trigger themeChange flag
        const state = getState();
        dispatch({
            type: 'EDIT_STORE_CHANGED',
            themeChange: true,
            themeChangeCategory: 'SBEM',  // Mark as SBEM category change
            configChange: state.save ? state.save.configChange : false,
            thirdpartyChange: state.save ? state.save.thirdpartyChange : false,
            payload: { type: ActionTypes.INCONTEXT_THEME_UPDATE_LIGHT, key, value }
        });
    },

    updateFontsUsage: (fonts) => (dispatch, getState) => {
        dispatch({
            type: ActionTypes.INCONTEXT_THEME_UPDATE_FONTS,
            payload: { fonts }
        });
        
        // Also dispatch EDIT_STORE_CHANGED to trigger themeChange flag
        const state = getState();
        dispatch({
            type: 'EDIT_STORE_CHANGED',
            themeChange: true,
            themeChangeCategory: 'SBEM',
            configChange: state.save ? state.save.configChange : false,
            thirdpartyChange: state.save ? state.save.thirdpartyChange : false,
            payload: { type: ActionTypes.INCONTEXT_THEME_UPDATE_FONTS, fonts }
        });
    },

    calculateInContextFontUsage: () => (dispatch, getState) => {
        const state = getState();
        const availableFonts = state.theme.fonts || {};
        const inContextTheme = state.app.data.inContextTheme;
        const calculatedFontsUsage = calculateInContextFontUsage(inContextTheme, availableFonts);
        
        dispatch({
            type: ActionTypes.INCONTEXT_THEME_UPDATE_FONTS,
            payload: { fonts: calculatedFontsUsage }
        });
    }
};

const inContextThemeReducer = handleActions(
    {
        [ActionTypes.INCONTEXT_THEME_UPDATE_LIGHT]: (state, action) => {
            const { key, value } = action.payload;
            let processedValue = value;
            
            if (key.startsWith('shadow-')) {
                if (key === 'shadow-color' || key === 'shadow-opacity') {
                    processedValue = value;
                } else {
                    processedValue = `${value}px`;
                }
            } else if (key === 'spacing' || key === 'radius') {
                processedValue = `${value}rem`;
            }
            
            const updatedLightTheme = {
                ...state.light,
                [key]: processedValue
            };

            // If any shadow property changed, update all shadow sizes
            const shadowPropertyKeys = ['shadow-color', 'shadow-opacity', 'shadow-offset-x', 'shadow-offset-y', 'shadow-blur-radius', 'shadow-spread'];
            if (shadowPropertyKeys.includes(key)) {
                const shadowProps = {
                    'shadow-color': key === 'shadow-color' ? processedValue : state.light['shadow-color'],
                    'shadow-opacity': key === 'shadow-opacity' ? processedValue : state.light['shadow-opacity'],
                    'shadow-offset-x': key === 'shadow-offset-x' ? processedValue : state.light['shadow-offset-x'],
                    'shadow-offset-y': key === 'shadow-offset-y' ? processedValue : state.light['shadow-offset-y'],
                    'shadow-blur-radius': key === 'shadow-blur-radius' ? processedValue : state.light['shadow-blur-radius'],
                    'shadow-spread': key === 'shadow-spread' ? processedValue : state.light['shadow-spread']
                };

                const shadowSizes = generateShadowSizes(shadowProps);
                
                return {
                    ...state,
                    light: {
                        ...updatedLightTheme,
                        ...shadowSizes
                    }
                };
            }
            
            return {
                ...state,
                light: updatedLightTheme
            };
        },

        [ActionTypes.INCONTEXT_THEME_UPDATE_FONTS]: (state, action) => {
            const { fonts } = action.payload;
            
            return {
                ...state,
                fontsUsage: fonts
            };
        },

        [ActionTypes.RESET_THEME_COLORS]: (state, action) => {
            return initialState;
        },

        [ActionTypes.LOGIN_SET_IN_CONTEXT_THEME]: (state, action) => {
            if (action.theme) {
                // Handle nested structure - merge with current state
                let newState = {
                    light: { ...state.light, ...(action.theme.light || {}) },
                    dark: { ...state.dark, ...(action.theme.dark || {}) },
                    fontsUsage: { ...state.fontsUsage, ...(action.theme.fontsUsage || {}) }
                };
                
                // Regenerate shadow sizes based on the loaded theme's individual shadow properties
                const lightTheme = newState.light;
                const shadowProps = {
                    'shadow-color': lightTheme['shadow-color'] || state.light['shadow-color'],
                    'shadow-opacity': lightTheme['shadow-opacity'] || state.light['shadow-opacity'],
                    'shadow-offset-x': lightTheme['shadow-offset-x'] || state.light['shadow-offset-x'],
                    'shadow-offset-y': lightTheme['shadow-offset-y'] || state.light['shadow-offset-y'],
                    'shadow-blur-radius': lightTheme['shadow-blur-radius'] || state.light['shadow-blur-radius'],
                    'shadow-spread': lightTheme['shadow-spread'] || state.light['shadow-spread']
                };

                const shadowSizes = generateShadowSizes(shadowProps);
                
                return {
                    ...newState,
                    light: {
                        ...newState.light,
                        ...shadowSizes
                    }
                };
            }
            return state;
        }
    },
    initialState
);

export {  inContextThemeReducer, actions };



// WEBPACK FOOTER //
// ./src/store/InContextThemeStore.js